/**
 * Deploy-time configuration for the browser.
 *
 * One web image serves every environment — the exact image dev ran is the one
 * prod gets — so anything that differs per environment cannot be baked in at
 * build time. The container writes `/config.js` from its own environment
 * variables when it starts (apps/web/docker-entrypoint.sh), and index.html
 * loads it before the app, as `window.__APP_CONFIG__`.
 *
 * Build-time `VITE_*` values remain as the fallback, so local builds and any
 * image predating this keep working unchanged.
 */
export interface RuntimeConfig {
  sentryDsn?: string;
  sentryEnvironment?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: RuntimeConfig;
  }
}

export function readRuntimeConfig(): RuntimeConfig {
  return (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};
}

export interface BuildSentryConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  mode: string;
}

export interface SentryConfig {
  dsn: string | undefined;
  environment: string;
  release: string;
}

/**
 * Runtime wins over build. An empty string counts as absent: an env var set to
 * nothing on the container must not blank out a working build-time value.
 */
export function resolveSentryConfig(runtime: RuntimeConfig, build: BuildSentryConfig): SentryConfig {
  const pick = (...vals: (string | undefined)[]) => vals.find((v) => v !== undefined && v !== '');
  return {
    dsn: pick(runtime.sentryDsn, build.dsn),
    environment: pick(runtime.sentryEnvironment, build.environment, build.mode) as string,
    // The release is the commit SHA, identical in every environment, so it
    // stays baked in — that is also what ties it to the uploaded source maps.
    release: pick(build.release) ?? 'local',
  };
}
